import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Button,
  Typography,
  Form,
  Input,
  Alert,
  Card,
} from "antd";
import { LockOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useChangePassword } from "../../hooks/useAuth";
import { useAuthStore } from "../../stores/authStore";

import "./ChangePassword.css";

const { Title, Paragraph } = Typography;
const { Content } = Layout;

const ChangePassword = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [success, setSuccess] = useState(false);
  const changePasswordMutation = useChangePassword();
  const { logout } = useAuthStore();

  const onFinish = async (values) => {
    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      setSuccess(true);
      form.resetFields();

      // Logout and redirect to sign-in after 2 seconds
      setTimeout(() => {
        logout();
        navigate("/sign-in");
      }, 2000);
    } catch (error) {
      console.error("Password change error:", error);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <Layout className="layout-default layout-change-password">
      <Content className="change-password-page">
        <Card className="change-password-card">
          {success ? (
            <>
              <div style={{ textAlign: "center" }}>
                <CheckCircleOutlined
                  style={{
                    fontSize: 72,
                    color: "#52c41a",
                    marginBottom: 24,
                  }}
                />
              </div>
              <Alert
                message="Password Changed Successfully!"
                description="Your password has been changed. You will be logged out shortly."
                type="success"
                showIcon
                style={{ marginBottom: 24 }}
              />
            </>
          ) : (
            <>
              <Title className="mb-15" level={2}>
                Change Password
              </Title>
              <Paragraph className="text-muted mb-24">
                You are required to change your password to continue.
              </Paragraph>

              {/* Error Message */}
              {changePasswordMutation.error && (
                <Alert
                  message="Change Password Failed"
                  description={
                    changePasswordMutation.error?.response?.data?.message ||
                    changePasswordMutation.error?.message ||
                    "Failed to change password. Please try again."
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <Form
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                className="change-password-form"
                form={form}
              >
                <Form.Item
                  label="Current Password"
                  name="currentPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please input your current password!",
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter current password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="New Password"
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please input your new password!",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters!",
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter new password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm New Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your new password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("The two passwords do not match!")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm new password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    size="large"
                    loading={changePasswordMutation.isLoading}
                  >
                    Change Password
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Card>
      </Content>
    </Layout>
  );
};

export default ChangePassword;
